import React, { useState, useEffect, useCallback } from 'react';
import { useTurnstile } from "@/components/Turnstile";
import axios from 'axios';
import { getPlatformToken } from '@/lib/musicApi';
import { useLanguage } from '@/contexts/LanguageContext';
import { Trans } from 'react-i18next';
import { trackClick } from '@/lib/track';
import { X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// External trigger (mirrors the CookieConsent openCookiePreferences pattern) so the
// Blog "FOLLOW" button can open this same popup without a separate form.
export const OPEN_BLOG_SUBSCRIBE_EVENT = 'samtwo:open-blog-subscribe';
export const openBlogSubscribe = () => window.dispatchEvent(new Event(OPEN_BLOG_SUBSCRIBE_EVENT));

const EmailSubscription = () => {
  const { t } = useLanguage();
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [isReturning, setIsReturning] = useState(false);

  const handleShow = useCallback(() => {
    const dismissed = sessionStorage.getItem('samtwo_sub_dismissed');
    if (dismissed) return;
    setShow(true);
  }, []);

  useEffect(() => {
    // Check if returning visitor
    const visited = localStorage.getItem('samtwo_visited');
    if (visited) setIsReturning(true);
    localStorage.setItem('samtwo_visited', 'true');

    // Show after 45 seconds or 60% scroll
    const timer = setTimeout(handleShow, 45000);

    const handleScroll = () => {
      const scrollPct = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      if (scrollPct > 0.6) handleShow();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleShow]);

  useEffect(() => {
    // Explicit open from the Blog FOLLOW button: force-show even if auto-popup was dismissed.
    const open = () => { setStatus('idle'); setShow(true); };
    window.addEventListener(OPEN_BLOG_SUBSCRIBE_EVENT, open);
    return () => window.removeEventListener(OPEN_BLOG_SUBSCRIBE_EVENT, open);
  }, []);

  const tsNewsletter = useTurnstile("newsletter");
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === 'loading') return;
    setStatus('loading');
    try {
      const tok = getPlatformToken();
      // Signed-in staff carry their token so the pre-launch gate recognises them; signed-out requests are unchanged.
      await axios.post(`${API}/subscribe`, { email, source: 'blog', turnstile_token: await tsNewsletter() },
        tok ? { headers: { Authorization: `Bearer ${tok}` } } : undefined);
      trackClick('email_signup', { source: 'blog' });
      setStatus('success');
      // Do not auto-close: the confirmation instructions must stay until the user
      // dismisses (X or click outside). Mark dismissed so the auto-popup won't retrigger.
      sessionStorage.setItem('samtwo_sub_dismissed', 'true');
    } catch {
      setStatus('error');
    }
  };

  const dismiss = () => {
    setShow(false);
    sessionStorage.setItem('samtwo_sub_dismissed', 'true');
  };

  if (!show) return null;

  return (
    <div data-testid="email-subscription-popup" onClick={dismiss} className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(10,15,26,0.55)', backdropFilter: 'blur(8px)' }}>
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-md p-8 rounded-3xl border" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)', boxShadow: 'var(--elev-3)' }}>
        <button data-testid="popup-close" onClick={dismiss} className="absolute top-4 right-4" style={{ color: 'var(--text-secondary)' }}>
          <X size={20} />
        </button>

        {status === 'success' ? (
          <div className="text-center py-6" data-testid="subscribe-blog-success">
            <p className="text-lg font-semibold" style={{ color: 'var(--accent-primary)' }}>{t('subscribe.blogSuccess')}</p>
            <p className="mt-3 text-sm" style={{ color: 'var(--text-secondary)' }}>{t('subscribe.blogSuccessDetail')}</p>
          </div>
        ) : (
          <>
            <h3 className="text-h3" style={{ color: 'var(--text-primary)' }}>
              {isReturning ? t('subscribe.blogReturning') : t('subscribe.blogTitle')}
            </h3>
            <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              {t('subscribe.blogSubtitle')}
            </p>
            <form onSubmit={handleSubmit} className="mt-6 flex gap-2">
              <input
                data-testid="subscribe-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('subscribe.placeholder')}
                required
                className="flex-1 px-4 py-3 rounded-full text-sm outline-none transition-colors"
                style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              />
              <button
                data-testid="subscribe-submit"
                type="submit"
                disabled={status === 'loading'}
                className="btn-pill btn-primary"
              >
                {t('subscribe.blogCta')}
              </button>
            </form>
            <p className="mt-3 text-xs" style={{ color: 'var(--text-secondary)' }} data-testid="subscribe-blog-confirm-note">
              {t('subscribe.blogConfirmNote')}
            </p>
            <p className="mt-2 text-xs" style={{ color: 'var(--text-secondary)' }} data-testid="subscribe-gdpr-consent">
              <Trans i18nKey="subscribe.gdprConsent" components={{ 1: <a href="/privacy" className="underline" /> }} />
            </p>
            {status === 'error' && <p className="mt-2 text-xs" style={{ color: 'var(--error)' }}>{t('footer.subError')}</p>}
          </>
        )}
      </div>
    </div>
  );
};

export default EmailSubscription;
